import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, MapPin, Clock, Trophy, Star, UserX, AlertCircle } from 'lucide-react';
import { API, getLogoUrl } from '../config/api';
import { useLanguage } from '../contexts/LanguageContext';
import ShareButtons from '../components/ShareButtons';

const dayNames = {
  gr: ['Κυριακή', 'Δευτέρα', 'Τρίτη', 'Τετάρτη', 'Πέμπτη', 'Παρασκευή', 'Σάββατο'],
  en: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
};

const getDayName = (dateStr, language) => {
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return dayNames[language][d.getDay()];
  } catch { return ''; }
};

const formatDate = (dateStr) => {
  try {
    const parts = dateStr.split('-');
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  } catch { return dateStr; }
};

const PLAYOFF_RE = /playoff|final|semi|τελικ|ημιτελ|κατάταξ/i;

const SchedulePage = ({ season = 1 }) => {
  const [teamIds, setTeamIds] = useState({});
  const [games, setGames] = useState([]);
  const [notices, setNotices] = useState([]);
  const { t, language } = useLanguage();

  useEffect(() => {
    axios.get(`${API}/games?season=${season}`).then(res => setGames(res.data)).catch(console.error);
    axios.get(`${API}/schedule-notices?season=${season}`).then(res => setNotices(res.data.notices || [])).catch(console.error);
    axios.get(`${API}/teams?season=${season}`).then(res => setTeamIds(Object.fromEntries(res.data.map(tm => [tm.name, tm.has_logo ? tm.id : null])))).catch(console.error);
  }, [season]);

  const groupedByWeek = games.reduce((acc, game) => {
    const week = game.week || 0;
    if (!acc[week]) acc[week] = [];
    acc[week].push(game);
    return acc;
  }, {});

  const sortedWeeks = Object.keys(groupedByWeek).map(Number).sort((a, b) => a - b);

  const groupByDate = (weekGames) => {
    const byDate = {};
    weekGames.forEach(g => {
      if (!byDate[g.date]) byDate[g.date] = [];
      byDate[g.date].push(g);
    });
    Object.values(byDate).forEach(arr => arr.sort((a, b) => (a.time || '').localeCompare(b.time || '')));
    return Object.entries(byDate).sort(([a], [b]) => a.localeCompare(b));
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-12" data-testid="schedule-page">
      <div className="text-center mb-8">
        <h1 className="section-title mb-4">{t('scheduleTitle')}</h1>
        <p className="text-slate-600" data-testid="schedule-subtitle">{season === 2 ? (language === 'gr' ? 'Πρόγραμμα Αγώνων 2ης Σεζόν' : 'Season 2 Match Schedule') : t('scheduleSubtitle')}</p>
      </div>

      {/* Dynamic Notices from Admin */}
      {notices.length > 0 && (
        <div className="mb-8 space-y-3">
          {notices.map(notice => (
            <div key={notice.id} className="bg-amber-50 border border-amber-200 rounded-xl p-5 flex items-start gap-3" data-testid={`schedule-notice-${notice.id}`}>
              <AlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={22} />
              <div>
                {notice.title && <h3 className="font-bold text-amber-800 mb-1">{notice.title}</h3>}
                <p className="text-amber-700 text-sm">{notice.text}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {sortedWeeks.length === 0 ? (
        season === 2 ? (
          <div className="bg-slate-50 border-2 border-dashed border-slate-200 rounded-2xl p-12 text-center" data-testid="schedule-season2-empty">
            <Calendar size={48} className="mx-auto text-slate-300 mb-4" />
            <h2 className="text-xl font-display font-bold text-slate-400 mb-2">{language === 'gr' ? 'Σύντομα κοντά σας!' : 'Coming Soon!'}</h2>
            <p className="text-slate-400 max-w-md mx-auto">{language === 'gr' ? 'Το πρόγραμμα της 2ης Σεζόν δεν έχει ανακοινωθεί ακόμα.' : 'The Season 2 schedule has not been announced yet.'}</p>
          </div>
        ) : <p className="text-center text-slate-500 py-12">{t('noCompletedGames')}</p>
      ) : (
        sortedWeeks.map((week, wi) => {
          const weekGames = groupedByWeek[week];
          const isPlayoff = PLAYOFF_RE.test(weekGames[0]?.phase || '');
          const regularWeeks = sortedWeeks.filter(w => !PLAYOFF_RE.test(groupedByWeek[w][0]?.phase || ''));
          const half = Math.ceil(regularWeeks.length / 2);
          const prevPlayoff = wi > 0 && PLAYOFF_RE.test(groupedByWeek[sortedWeeks[wi - 1]][0]?.phase || '');
          let roundLabel = null;
          if (season !== 1) {
            if (!isPlayoff && week === regularWeeks[0]) roundLabel = language === 'gr' ? 'Κανονική περίοδος – Α΄ Γύρος' : 'Regular Season – Round 1';
            else if (!isPlayoff && regularWeeks.length > 1 && week === regularWeeks[half]) roundLabel = language === 'gr' ? 'Κανονική περίοδος – Β΄ Γύρος' : 'Regular Season – Round 2';
            else if (isPlayoff && !prevPlayoff) roundLabel = 'Playoffs';
          }
          const byeTeam = weekGames[0]?.bye_team || '';

          // Group games by phase within the same week
          const phaseGroups = {};
          weekGames.forEach(g => {
            const phase = g.phase || '';
            if (!phaseGroups[phase]) phaseGroups[phase] = [];
            phaseGroups[phase].push(g);
          });
          const phases = Object.keys(phaseGroups);
          const hasMixedPhases = phases.length > 1;

          return (
            <div key={week} className="mb-10" data-testid={`week-${week}`}>
              {roundLabel && (
                <div className="flex items-center gap-4 mb-6 mt-2" data-testid={`round-label-${week}`}>
                  <span className="h-px flex-1 bg-gradient-to-r from-transparent to-[#F97316]/50" />
                  <span className="px-4 py-1.5 rounded-full bg-[#1E3A5F] text-white text-sm font-bold tracking-wide">{roundLabel}</span>
                  <span className="h-px flex-1 bg-gradient-to-l from-transparent to-[#F97316]/50" />
                </div>
              )}
              {/* Week Header - show only if single phase */}
              {!hasMixedPhases && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
                  <h2 className={`text-xl font-display font-bold flex items-center ${isPlayoff ? 'text-[#F97316]' : 'text-[#1E3A5F]'}`}>
                    {isPlayoff ? <Trophy className="mr-2 text-[#F97316]" size={24} /> : <Calendar className="mr-2" size={24} />}
                    {phases[0]}
                  </h2>
                </div>
              )}

              {/* Bye team */}
              {byeTeam && (
                <div className="mb-5 bg-slate-100 border border-slate-200 rounded-xl px-5 py-3 flex items-center gap-3" data-testid={`bye-${week}`}>
                  {teamIds[byeTeam] ? (
                    <img src={getLogoUrl(teamIds[byeTeam])} alt={byeTeam} className="w-10 h-10 object-contain rounded-full bg-white p-0.5" data-testid={`bye-logo-${week}`} />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-slate-300 flex items-center justify-center">
                      <UserX size={18} className="text-slate-600" />
                    </div>
                  )}
                  <div>
                    <span className="text-xs uppercase tracking-wider text-slate-400 font-semibold">
                      {language === 'gr' ? 'Ρεπό' : 'Bye'}
                    </span>
                    <p className="text-base font-bold text-slate-700">{byeTeam}</p>
                  </div>
                </div>
              )}

              {/* Render games grouped by phase */}
              {phases.map(phase => {
                const phaseGames = phaseGroups[phase];
                const phaseDateGroups = groupByDate(phaseGames);
                return (
                  <div key={phase} className="mb-4">
                    {/* Phase sub-header for mixed-phase weeks */}
                    {hasMixedPhases && (
                      <div className="flex items-center gap-3 mb-3 mt-2">
                        <h2 className="text-lg font-display font-bold flex items-center text-[#F97316]">
                          <Trophy className="mr-2 text-[#F97316]" size={22} />
                          {phase}
                        </h2>
                      </div>
                    )}
                    {/* 9th team auto-advance note for Placement 7-9 */}
                    {phase.includes('Κατάταξης Θέσεων 7') && !phase.includes('Τελικός') && (
                      <div className="mb-4 bg-blue-50 border border-blue-200 rounded-xl px-5 py-3 flex items-start gap-3" data-testid={`placement-note-${week}`}>
                        <div className="w-9 h-9 rounded-full bg-blue-200 flex items-center justify-center flex-shrink-0">
                          <Star size={18} className="text-blue-700" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-blue-800">
                            {language === 'gr' 
                              ? 'Η 7η ομάδα περνά αυτόματα στον Τελικό Κατάταξης Θέσεων 7–9' 
                              : 'The 7th place team automatically advances to the Placement 7–9 Final'}
                          </p>
                        </div>
                      </div>
                    )}
                    {phaseDateGroups.map(([date, dateGames]) => (
                      <div key={date} className="mb-6">
                        {!hasMixedPhases && (
                          <div className="flex items-center gap-2 mb-3 ml-1">
                            <span className="font-semibold text-[#1E3A5F]">{getDayName(date, language)}</span>
                            <span className="text-slate-400">{formatDate(date)}</span>
                          </div>
                        )}
                        {hasMixedPhases && phaseDateGroups.length > 0 && (
                          <div className="flex items-center gap-2 mb-3 ml-1">
                            <span className="font-semibold text-[#1E3A5F]">{getDayName(date, language)}</span>
                            <span className="text-slate-400">{formatDate(date)}</span>
                          </div>
                        )}
                        <div className="grid gap-3">
                          {dateGames.map(game => {
                            const homeTBD = !game.home_team_id || game.home_team_id === 'TBD';
                            const awayTBD = !game.away_team_id || game.away_team_id === 'TBD';
                            const bothTBD = homeTBD && awayTBD;
                            return (
                            <div key={game.id} className={`card p-4 md:p-5 ${isPlayoff ? 'border-l-4 border-[#F97316]' : ''}`} data-testid={`schedule-game-${game.id}`}>
                              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                                <div className="flex items-center gap-3 text-sm text-slate-500 min-w-[100px]">
                                  <span className="flex items-center"><Clock size={16} className="mr-1" /> {game.time}</span>
                                </div>
                                <div className="flex items-center justify-center gap-3 flex-1">
                                  {bothTBD ? (
                                    <div className="flex items-center gap-2">
                                      <Star className="text-[#F97316]" size={18} />
                                      <span className="font-bold text-[#1E3A5F]">{game.home_team_name} vs {game.away_team_name}</span>
                                    </div>
                                  ) : (
                                    <>
                                      <div className="flex items-center gap-2 justify-end flex-1">
                                        <span className="font-bold text-right">{game.home_team_name}</span>
                                        {!homeTBD && <img src={getLogoUrl(game.home_team_id)} alt="" className="w-8 h-8 object-contain rounded" onError={e => { e.target.style.display = 'none'; }} />}
                                        {homeTBD && <Star className="text-[#F97316] flex-shrink-0" size={18} />}
                                      </div>
                                      <span className="px-3 py-1.5 bg-slate-100 rounded-lg font-bold text-slate-500 text-sm">{t('vs')}</span>
                                      <div className="flex items-center gap-2 justify-start flex-1">
                                        {!awayTBD && <img src={getLogoUrl(game.away_team_id)} alt="" className="w-8 h-8 object-contain rounded" onError={e => { e.target.style.display = 'none'; }} />}
                                        {awayTBD && <Star className="text-[#F97316] flex-shrink-0" size={18} />}
                                        <span className="font-bold text-left">{game.away_team_name}</span>
                                      </div>
                                    </>
                                  )}
                                </div>
                                <div className="hidden md:flex items-center text-sm text-slate-400 min-w-[200px] justify-end">
                                  <MapPin size={14} className="mr-1 flex-shrink-0" /> <span className="truncate">{game.venue}</span>
                                </div>
                              </div>
                            </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          );
        })
      )}
      <div className="mt-4 flex justify-end">
        <ShareButtons title={language === 'gr' ? 'Πρόγραμμα Αγώνων WBLA' : 'WBLA Schedule'} />
      </div>
    </div>
  );
};

export default SchedulePage;
